import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { SellerBalanceById } from "@/components/SellerBalance";
import { PageHeader, DataTable, EmptyState, StatCard } from "@/components/ui-system";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, PackageX, RotateCcw } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";
import { useLocalStorageState } from "@/hooks/useLocalStorageState";

const PAGE_SIZE = 50;
const ALL = "all";
const DEFAULT_WINDOW_DAYS = 30;
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const inr = (n) => `₹${Number(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const errDetail = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return e?.response?.data?.message || fb;
};

const B2CNdr = ({ user, onLogout }) => {
  const navigate = useNavigate();

  const [sellerId, setSellerId] = useLocalStorageState("admin-b2c-ndr:seller", ALL);
  const [aggregator, setAggregator] = useLocalStorageState("admin-b2c-ndr:aggregator", ALL);
  const [dateFrom, setDateFrom] = useLocalStorageState("admin-b2c-ndr:date_from", daysAgoISO(DEFAULT_WINDOW_DAYS));
  const [dateTo, setDateTo] = useLocalStorageState("admin-b2c-ndr:date_to", "");
  const [search, setSearch] = useLocalStorageState("admin-b2c-ndr:search", "");
  const [searchInput, setSearchInput] = useState(search);
  const [page, setPage] = useState(1);

  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState({ sellers: [], aggregators: [] });
  const abortRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/b2c/admin/ndr/filter-options`)
      .then((r) => { if (!cancelled) setOptions({ sellers: r.data?.sellers || [], aggregators: r.data?.aggregators || [] }); })
      .catch(() => { if (!cancelled) setOptions({ sellers: [], aggregators: [] }); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput, setSearch]);

  const fetchNdr = useCallback(async () => {
    setLoading(true);
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (sellerId && sellerId !== ALL) params.seller_id = sellerId;
      if (aggregator && aggregator !== ALL) params.aggregator = aggregator;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (search) params.search = search;
      const { data: res } = await axios.get(`${API}/b2c/admin/ndr`, { params, signal: controller.signal });
      setData({ items: res.items || [], total: res.total || 0 });
    } catch (e) {
      if (axios.isCancel?.(e) || e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
      toast.error(errDetail(e, "Failed to load NDR shipments"));
    } finally {
      setLoading(false);
    }
  }, [page, sellerId, aggregator, dateFrom, dateTo, search]);

  useEffect(() => { fetchNdr(); }, [fetchNdr]);
  useEffect(() => { setPage(1); }, [sellerId, aggregator, dateFrom, dateTo, search]);

  const resetFilters = () => {
    setSellerId(ALL); setAggregator(ALL);
    setDateFrom(daysAgoISO(DEFAULT_WINDOW_DAYS)); setDateTo(""); setSearch(""); setSearchInput("");
  };
  const hasFilters =
    (sellerId && sellerId !== ALL) || (aggregator && aggregator !== ALL) || !!dateTo || !!search ||
    dateFrom !== daysAgoISO(DEFAULT_WINDOW_DAYS);

  const columns = [
    { key: "order", header: "Order", primary: true,
      cell: (r) => (
        <button type="button" onClick={(e) => { e.stopPropagation(); navigate(`/admin/b2c/orders/${r.id}`); }}
          className="text-sm font-medium text-brand hover:underline">{r.order_number}</button>
      ) },
    { key: "date", header: "Date", hideOnMobile: true,
      cell: (r) => <span className="whitespace-nowrap text-sm text-slate-600">{formatToIST(r.created_at, false)}</span> },
    { key: "seller", header: "Seller", cell: (r) => (
      <div>
        <span className="text-sm font-medium text-slate-800">{r.seller_brand_name || r.seller_id || "-"}</span>
        <SellerBalanceById sellerId={r.seller_id} />
      </div>
    ) },
    { key: "customer", header: "Customer",
      cell: (r) => <div className="text-sm"><div className="font-medium text-slate-800">{r.buyer_name || "-"}</div><div className="text-xs text-slate-500">{[r.buyer_city, r.buyer_phone].filter(Boolean).join(" · ")}</div></div> },
    { key: "courier", header: "Courier", hideOnMobile: true, cell: (r) => <span className="text-sm text-slate-700">{r.courier_name || "-"}</span> },
    { key: "aggregator", header: "Aggregator", hideOnMobile: true,
      cell: (r) => r.aggregator ? <span className="text-sm capitalize text-slate-700">{r.aggregator}</span> : <span className="text-sm text-slate-400">-</span> },
    { key: "payment", header: "Payment", hideOnMobile: true,
      cell: (r) => r.payment_mode === "cod"
        ? <span className="text-sm font-medium text-amber-700">COD {inr(r.cod_amount)}</span>
        : <span className="text-sm text-slate-600">Prepaid</span> },
    { key: "reason", header: "NDR reason", cell: (r) => <span className="text-sm text-slate-700">{r.ndr_reason || <span className="text-slate-400">—</span>}</span> },
    { key: "lastaction", header: "Last action", hideOnMobile: true,
      cell: (r) => {
        const a = (r.ndr_actions || [])[(r.ndr_actions || []).length - 1];
        return a ? <div className="text-xs text-slate-600"><Badge variant="outline" className="border-slate-200">{a.action}</Badge><div className="mt-0.5 text-slate-400">{formatToIST(a.at)}</div></div> : <span className="text-slate-400 text-sm">—</span>;
      } },
  ];

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full">
        <PageHeader title="NDR — Failed Deliveries" subtitle="Cross-seller undelivered shipments — courier reason & seller action history (read-only)" />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard label="Open NDR shipments" value={Number(data.total ?? 0).toLocaleString("en-IN")} icon={PackageX} accent="warning" loading={loading} trend="Across all sellers in range" />
        </div>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Seller</Label>
                <Select value={sellerId} onValueChange={setSellerId}>
                  <SelectTrigger><SelectValue placeholder="All sellers" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All sellers</SelectItem>
                    {options.sellers.map((s) => <SelectItem key={s.seller_id} value={s.seller_id}>{s.brand_name || s.seller_id}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Aggregator</Label>
                <Select value={aggregator} onValueChange={setAggregator}>
                  <SelectTrigger><SelectValue placeholder="All aggregators" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All aggregators</SelectItem>
                    {options.aggregators.map((a) => <SelectItem key={a} value={a} className="capitalize">{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600" htmlFor="admin-ndr-from">From</Label>
                <Input id="admin-ndr-from" type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600" htmlFor="admin-ndr-to">To</Label>
                <Input id="admin-ndr-to" type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600" htmlFor="admin-ndr-search">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                  <Input id="admin-ndr-search" placeholder="Order #, AWB or phone..." value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)} className="pl-9" />
                </div>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500">Showing the last {DEFAULT_WINDOW_DAYS} days by default — widen “From” to see older NDRs.</p>
              {hasFilters && (
                <Button type="button" variant="ghost" size="sm" onClick={resetFilters}>
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Clear filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <DataTable
          columns={columns}
          data={data.items}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/admin/b2c/orders/${r.id}`)}
          loading={loading}
          empty={<EmptyState icon={PackageX} title="No failed deliveries in range" description={hasFilters ? "Try widening the seller, aggregator or date filters." : "No NDR shipments across any seller in the selected window."} action={hasFilters ? <Button type="button" variant="outline" onClick={resetFilters}><RotateCcw className="w-4 h-4 mr-2" /> Clear filters</Button> : undefined} />}
          pagination={totalPages > 1 ? { page, pages: totalPages, total: data.total, pageSize: PAGE_SIZE, onPageChange: setPage } : undefined}
        />
      </div>
    </AdminLayout>
  );
};

export default B2CNdr;
